/**
 * "Which one of these is you?" — the way back in for a phone the room does not
 * recognise (SPEC §5.4).
 *
 * The session token in localStorage is what normally makes a reconnect silent,
 * and there are ordinary reasons for a phone not to have one: a private window,
 * a browser that clears on close, iOS evicting the site's data, or a dead phone
 * replaced by somebody's spare. Without this, that player is locked out of a
 * game they are sitting in — the room can see their face on the board and will
 * not let them near it.
 *
 * So the room offers the seats nobody is in, as license cards, and they point
 * at themselves. It is not a login and does not pretend to be: everyone who can
 * reach this screen already has the room code, which means they are in the
 * room. The check that matters here is the one everybody in the room is already
 * making, out loud, at the table.
 *
 * The server decides what appears here — only offline, un-removed seats that
 * got as far as a photo — and re-checks the claim when it lands, because two
 * phones can be looking at this at once. When it refuses, `seat_taken` surfaces
 * on the Shell's own error toast and a fresh offer replaces the list; there is
 * no second copy of that message here (DESIGN §4 allows the app one).
 */
import { useLocation } from 'react-router-dom';
import { useClaimSeat, useClaimableSeats, usePhase } from '../../store/index.js';
import { LicenseCard } from '../../components/index.js';
import { Body, Button, Notice, Screen, ScreenTitle } from '../../app/ui.js';

export function ClaimSeatScreen() {
  const offer = useClaimableSeats();
  const claimSeat = useClaimSeat();
  const phase = usePhase();
  const { pathname } = useLocation();

  if (!offer) return null;
  const { seats, canJoinAsNew } = offer;
  const reopeningResults =
    phase === 'superlatives' ||
    phase === 'ended' ||
    pathname.endsWith('/superlatives') ||
    pathname.endsWith('/ended');

  return (
    <Screen
      eyebrow={reopeningResults ? 'Game over' : 'Welcome back'}
      testId="claim-seat"
      bodyClassName="gap-4 overflow-y-auto"
      footer={
        canJoinAsNew ? (
          <Button variant="quiet" onClick={() => claimSeat()} data-testid="claim-none">
            None of these — I&rsquo;m new
          </Button>
        ) : (
          // Mid-game there is no new seat to be had: the card set is fixed at
          // game start and every ranking already refers to it (SPEC §5.4).
          <Notice>The game has started, so the only way in is an empty seat.</Notice>
        )
      }
    >
      <ScreenTitle>{reopeningResults ? 'Choose your card.' : 'Which one is you?'}</ScreenTitle>
      <Body>
        {reopeningResults
          ? 'Pick your card to reopen the results.'
          : 'This phone doesn’t remember the room. Pick your card and you’ll pick up exactly where you left off.'}
      </Body>

      <ul className="flex flex-col gap-2" data-testid="claim-seat-list">
        {seats.map((seat) => (
          <li key={seat.id}>
            <button
              type="button"
              onClick={() => claimSeat(seat.id)}
              className="w-full text-left"
              data-testid={`claim-seat-${seat.id}`}
            >
              {/* No "offline" suffix: every seat on this screen is empty, so
                  saying so twelve times says nothing. */}
              <LicenseCard player={seat} variant="chip" />
            </button>
          </li>
        ))}
        {seats.length === 0 ? (
          <li className="rounded-card border border-dashed border-line px-4 py-6 text-center text-[0.8125rem] text-ink-soft">
            Every seat is taken.
          </li>
        ) : null}
      </ul>
    </Screen>
  );
}
